import { Box, Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react'
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faFunction,
  faH2,
  faH3,
  faHeading,
  faImage,
  faItalic,
  faLink,
  faList,
  faListOl,
  faMicrophone,
  faMicrophoneSlash,
  faSmile,
  faSpinner,
  faUnderline,
} from '@fortawesome/pro-regular-svg-icons'
import React, {
  type ChangeEvent,
  type ReactElement,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react'
import { useSlate } from 'slate-react'

import type { TranscriptFragment } from '@/components/Conversation/hooks/useSpeechRecognition/types'
import useSpeechRecognition from '@/components/Conversation/hooks/useSpeechRecognition/useSpeechRecognition'
import EmojiPicker from '@/components/EmojiPicker/EmojiPicker'
import Tippy from '@/components/UI/Tippy'

import { isAlignActive, toggleAlign } from './align'
import { isBlockActive, toggleBlock } from './block'
import CreateOrUpdateHyperlinkModal from './CreateOrUpdateHyperlinkModal'
import CreateOrUpdateMathModal from './CreateOrUpdateMathModal'
import { ACCEPTED_FILE_TYPES } from './constants'
import { isImageActive } from './image'
import { getActiveLinkUrl, isLinkActive } from './inlines'
import { isMarkActive, toggleMark } from './mark'
import {
  DEFAULT_MATH_DETAILS,
  getActiveMathElementDetails,
  isMathActive,
  type MathElementDetails,
} from './math'
import ResizeImageModal from './ResizeImageModal'
import ToolbarButton from './ToolbarButton'
import UploadImageModal from './UploadImageModal'

export function LinkButton(): ReactElement {
  const editor = useSlate()
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [link, setLink] = useState('')

  return (
    <>
      <CreateOrUpdateHyperlinkModal
        link={link}
        setLink={setLink}
        showModal={showLinkModal}
        setShowModal={setShowLinkModal}
      />
      <ToolbarButton
        active={isLinkActive(editor)}
        onMouseDown={() => {
          setLink(getActiveLinkUrl(editor) ?? '')
          setShowLinkModal(true)
        }}
        icon={faLink}
      />
    </>
  )
}

export function MathButton(): ReactElement {
  const editor = useSlate()
  const [showMathModal, setShowMathModal] = useState(false)
  const [math, setMath] = useState<MathElementDetails>(DEFAULT_MATH_DETAILS)

  return (
    <>
      <CreateOrUpdateMathModal
        math={math}
        setMath={setMath}
        showModal={showMathModal}
        setShowModal={setShowMathModal}
      />
      <ToolbarButton
        active={isMathActive(editor)}
        onMouseDown={() => {
          setMath(getActiveMathElementDetails(editor) ?? DEFAULT_MATH_DETAILS)
          setShowMathModal(true)
        }}
        icon={faFunction}
      />
    </>
  )
}

export function ImageButton(): ReactElement {
  const editor = useSlate()

  const [showResizeModal, setShowResizeModal] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [imageUpload, setImageUpload] = useState<File | null>(null)

  return (
    <>
      <UploadImageModal
        imageUpload={imageUpload}
        setImageUpload={setImageUpload}
        showModal={showImageModal}
        setShowModal={setShowImageModal}
      />
      <ResizeImageModal showModal={showResizeModal} setShowModal={setShowResizeModal} />
      <ToolbarButton
        active={isImageActive(editor)}
        onMouseDown={() => {
          if (isImageActive(editor)) {
            setShowResizeModal(true)
          } else {
            setImageUpload(null)
            setShowImageModal(true)
          }
        }}
        icon={faImage}
      />
    </>
  )
}

export function BoldButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isMarkActive(editor, 'bold')}
      onMouseDown={() => toggleMark(editor, 'bold')}
      icon={faBold}
    />
  )
}

export function ItalicButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isMarkActive(editor, 'italic')}
      onMouseDown={() => toggleMark(editor, 'italic')}
      icon={faItalic}
    />
  )
}

export function UnderlineButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isMarkActive(editor, 'underline')}
      onMouseDown={() => toggleMark(editor, 'underline')}
      icon={faUnderline}
    />
  )
}

export function HeadingButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isBlockActive(editor, 'heading')}
      onMouseDown={() => toggleBlock(editor, 'heading')}
      icon={faHeading}
    />
  )
}

export function BulletedListButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isBlockActive(editor, 'bulleted-list')}
      onMouseDown={() => toggleBlock(editor, 'bulleted-list')}
      icon={faList}
    />
  )
}

export function Heading2Button(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isBlockActive(editor, 'heading-two')}
      onMouseDown={() => toggleBlock(editor, 'heading-two')}
      icon={faH2}
    />
  )
}

export function Heading3Button(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isBlockActive(editor, 'heading-three')}
      onMouseDown={() => toggleBlock(editor, 'heading-three')}
      icon={faH3}
    />
  )
}

export function NumberedListButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isBlockActive(editor, 'numbered-list')}
      onMouseDown={() => toggleBlock(editor, 'numbered-list')}
      icon={faListOl}
    />
  )
}

export function LeftAlignButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isAlignActive(editor, 'left')}
      onMouseDown={() => toggleAlign(editor, 'left')}
      icon={faAlignLeft}
    />
  )
}

export function CenterAlignButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isAlignActive(editor, 'center')}
      onMouseDown={() => toggleAlign(editor, 'center')}
      icon={faAlignCenter}
    />
  )
}

export function RightAlignButton(): ReactElement {
  const editor = useSlate()

  return (
    <ToolbarButton
      active={isAlignActive(editor, 'right')}
      onMouseDown={() => toggleAlign(editor, 'right')}
      icon={faAlignRight}
    />
  )
}

export function EmojiButton({
  isPopoverOffset = false,
}: {
  isPopoverOffset?: boolean
}): ReactElement {
  const { insertText } = useSlate()
  const { isOpen, onClose, onOpen } = useDisclosure()

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      offset={isPopoverOffset ? [0, 60] : undefined}
    >
      <PopoverTrigger>
        <Box>
          <ToolbarButton active={isOpen} icon={faSmile} />
        </Box>
      </PopoverTrigger>
      <PopoverContent>
        <EmojiPicker onSelect={insertText} />
      </PopoverContent>
    </Popover>
  )
}

export function ImageUploadButton({
  onUpload,
}: {
  onUpload: (files: File[]) => void
}): ReactElement {
  // We need a file input to let the user upload an image. So we can
  // use a button to interface with a hidden file input.
  const hiddenFileInput = React.useRef<HTMLInputElement>(null)

  const handleClick = () => {
    hiddenFileInput.current?.click()
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0) {
      return
    }
    onUpload(files)
    // Clear the file input so that the user can select the same file again,
    // if needed.
    event.target.value = ''
  }

  return (
    <>
      <ToolbarButton active={false} icon={faImage} onMouseDown={handleClick} />
      <input
        type="file"
        title="Image upload"
        ref={hiddenFileInput}
        onChange={handleChange}
        style={{ display: 'none' }}
        multiple
        accept={ACCEPTED_FILE_TYPES.join(',')}
      />
    </>
  )
}

export type MicrophoneButtonRef = {
  stopRecording: () => Promise<void>
}

export const MicrophoneButton = ({
  parentRef,
}: {
  parentRef?: React.RefObject<MicrophoneButtonRef>
}) => {
  const isMac = navigator.platform.toUpperCase().includes('MAC')
  const isWindows = navigator.platform.toUpperCase().includes('WIN')

  const tooltipText = isMac
    ? "Try pressing 🌐 twice on your keyboard to use your computer's voice to text instead!"
    : isWindows
      ? "Try pressing Windows Key + H to use your computer's voice to text instead!"
      : ''

  const tooltipErrorText = 'Voice to Text encountered an error. Try reloading the page.'

  const { insertText } = useSlate()

  // Microphone Button re-renders with every change to RichTextEditor body
  // Redefining this function on each render will cause useSpeechRecognition to mount/unmount
  const handleTranscriptFragment = useCallback(
    (fragment: TranscriptFragment) => {
      if (fragment.isFinal && fragment.isFormatted) {
        insertText(`${fragment.content} `)
      }
    },
    [insertText]
  )

  const { isRecording, startRecording, stopRecording, error, isUpdatingProviderState } =
    useSpeechRecognition({
      onTranscriptFragment: handleTranscriptFragment,
    })

  useImperativeHandle(
    parentRef,
    () => ({
      stopRecording: async () => {
        if (isRecording) {
          await stopRecording()
        }
      },
    }),
    [isRecording, stopRecording]
  )

  const handleMouseDown = useCallback(async () => {
    if (error !== null) {
      return
    }

    if (isRecording) {
      await stopRecording()
    } else {
      await startRecording()
    }
  }, [error, isRecording, startRecording, stopRecording])

  return (
    <Tippy
      content={error !== null ? tooltipErrorText : tooltipText}
      hasArrow
      disabled={error === null && !isMac && !isWindows}
    >
      <Box>
        <ToolbarButton
          active={isRecording}
          icon={
            isUpdatingProviderState ? faSpinner : error !== null ? faMicrophoneSlash : faMicrophone
          }
          isDisabled={isUpdatingProviderState}
          onMouseDown={handleMouseDown}
        />
      </Box>
    </Tippy>
  )
}
